import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import {
  useGetCustomerIamUser,
  UserBo
} from '@utils/api/back-office/customer-bo/use-get-iam-user.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import React, { useEffect, useState } from 'react';
import { Params, useParams } from 'react-router-dom';
import { useUpdateUserBo } from '@utils/api/back-office/customer-bo/use-update-user-bo.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useGetCustomerById } from '@utils/api/back-office/customer-bo/use-get-customer-bo.tsx';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context';

type UserFormData = {
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
};

type Props = {
  selectedUser: UserBo;
  isEditModalOpen: boolean;
  setIsEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function UpdateUserIamForm({
  selectedUser,
  isEditModalOpen,
  setIsEditModalOpen
}: Props) {
  const { t } = useTranslation();
  const { customerId } = useParams<Params>();
  const { getColour } = useThemeContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: user, isLoading } = useGetCustomerIamUser(
    Number(customerId),
    selectedUser?.id || 0
  );
  const { data: organization } = useGetCustomerById(Number(customerId));

  const updateUserMutation = useUpdateUserBo();

  const schema: yup.ObjectSchema<UserFormData> = yup
    .object({
      first_name: yup
        .string()
        .required(yup.ref('first_name') ? undefined : 'First name is required'),
      last_name: yup.string().required('Last name is required'),
      email: yup.string().email('Invalid email').required('Email is required'),
      roles: yup
        .array()
        .of(yup.string().required())
        .min(1, 'At least one role must be selected')
        .required()
        .default([])
    })
    .required();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<UserFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: user?.result.first_name || '',
      last_name: user?.result.last_name || '',
      email: user?.result.email || '',
      roles: user?.result.roles || []
    }
  });

  useEffect(() => {
    if (isEditModalOpen && !isLoading && user && user.result) {
      setValue('first_name', user.result.first_name || '');
      setValue('last_name', user.result.last_name || '');
      setValue('email', user.result.email || '');
      setValue('roles', user.result.roles || []);
    }
  }, [isEditModalOpen, isLoading, user, setValue]);

  const onSubmit = (data: UserFormData) => {
    if (selectedUser && Number(customerId) && selectedUser.id) {
      setIsSubmitting(true);
      updateUserMutation.mutate(
        {
          customerId: Number(customerId),
          userId: selectedUser.id.toString(),
          data
        },
        {
          onSuccess: () => {
            toast.success(t('user-updated-successfully'));
            setIsEditModalOpen(false);
            setIsSubmitting(false);
          },
          onError: (error: AxiosError<BaseError>) => {
            let messages: string[] = [];

            if (Array.isArray(error?.response?.data?.detail)) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              messages = error.response.data.detail.map((err: any) => {
                const field = err.loc?.[1] ?? 'field';
                return `${field}: ${err.msg}`;
              });
            } else if (error?.response?.data?.message) {
              messages = [error.response.data.message];
            } else {
              messages = [t('failed-to-update-the-user')];
            }
            setIsSubmitting(false);
            toast.error(messages.join('\n'));
          }
        }
      );
    }
  };

  const FORM_ID = 'update-iam-user-form';

  return (
    <Modal
      isOpen={isEditModalOpen}
      onClose={() => setIsEditModalOpen(false)}
      size="small"
      title={t(`${selectedUser.first_name} ${selectedUser.last_name}`)}
      footerActions={[
        <Button
          key="cancel"
          type="button"
          onClick={() => setIsEditModalOpen(false)}
          label={t('cancel')}
          size="small"
          variant="gray"
          disabled={isSubmitting}
        />,
        // Submit via form to get native Enter behavior
        <Button
          key="save"
          type="submit"
          form={FORM_ID}
          label={isSubmitting ? t('updating') : t('update')}
          variant="primary"
          size="small"
          disabled={isSubmitting}
        />
      ]}
    >
      {isLoading ? (
        <p
          className="text-sm text-center"
          style={{ color: getColour('inactiveText') }}
        >
          {t('loading')}...
        </p>
      ) : (
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          {/* Hidden submit ensures implicit Enter works from any input */}
          <button type="submit" className="hidden" aria-hidden="true" />

          <div className="mt-2 space-y-4">
            <Input
              id="first_name"
              label={t('first-name')}
              type="text"
              defaultValue={user?.result.first_name}
              autoComplete="first-name"
              className={cn('block w-full')}
              {...register('first_name')}
              error={errors?.first_name?.message}
            />
            <Input
              id="last_name"
              label={t('last-name')}
              type="text"
              defaultValue={user?.result.last_name}
              autoComplete="last-name"
              className={cn('block w-full')}
              {...register('last_name')}
              error={errors?.last_name?.message}
            />
            <Input
              id="email"
              label={t('email')}
              type="email"
              defaultValue={user?.result.email}
              autoComplete="email"
              className={cn('block w-full')}
              {...register('email')}
              error={errors?.email?.message}
            />

            <p className="mt-2 font-semibold text-sm">{t('role')}:</p>
            <div className="grid gap-2 w-1/2">
              <Checkbox
                id="admin"
                label={t('admin')}
                register={register('roles')}
                value="admin"
                variant="primary"
              />

              <Checkbox
                id="contributor"
                label={t('contributor')}
                register={register('roles')}
                value="contributor"
                variant="primary"
              />

              {organization?.result.is_reseller && (
                <Checkbox
                  id="reseller"
                  label={t('reseller')}
                  register={register('roles')}
                  value="reseller"
                  variant="primary"
                />
              )}

              {errors.roles && (
                <p className="text-red-500 text-sm">{errors.roles.message}</p>
              )}
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}
